import { BOOKMAKER_COLORS, SHARP_BOOK_NAMES } from './constants';

/**
 * Live odds data for a single bookmaker
 */
export interface LiveOdds {
  bookmaker: string;
  homePrice: number | null;
  awayPrice: number | null;
  isSharp: boolean;
  timestamp: string | null;
}

/**
 * Data point for the line chart
 */
export interface LineChartDataPoint {
  time: string;
  timeNum: number;
  [key: string]: string | number | null; // Dynamic keys for each bookmaker_home, bookmaker_away
}

/**
 * Data point for Polymarket price chart
 */
export interface PolymarketChartData {
  time: string;
  yes: number;
  no: number;
  timeNum: number;
  homeLabel: string;
  awayLabel: string;
}

/**
 * Data point for price gap chart
 */
export interface PriceGapChartData {
  time: string;
  gap: number;
  polymarket: number;
  odds: number;
  timeNum: number;
}

/**
 * Data point for signal strength chart
 */
export interface SignalChartData {
  time: string;
  strength: number;
  type: string;
  isActive: number;
  timeNum: number;
}

/**
 * Input types for odds history
 */
export interface OddsHistoryItem {
  bookmaker: string;
  timestamp: string;
  outcomeName: string;
  price: number;
  isSharp: boolean;
}

/**
 * Input types for price history
 */
export interface PriceHistoryItem {
  timestamp: string;
  outcome: string;
  midPrice: number;
  bidPrice?: number;
  askPrice?: number;
}

/**
 * Input types for signal data
 */
export interface SignalItem {
  id: string;
  signalType: string;
  strength: number;
  detectedAt: string;
  isActive?: boolean;
}

/**
 * Process odds history into line chart format with connected lines per bookmaker
 * Returns: lineChartData for the chart, liveOdds for the index panel, and list of bookmakers
 */
export function processOddsForLineChart(
  oddsHistory: OddsHistoryItem[],
  selectedBookmakers: string[] | null,
  homeTeam: string,
  awayTeam: string
): { lineChartData: LineChartDataPoint[]; liveOdds: LiveOdds[]; bookmakersInData: string[] } {
  // Filter by selected bookmakers if provided
  const filtered = selectedBookmakers
    ? oddsHistory.filter(o => selectedBookmakers.includes(o.bookmaker || 'Unknown'))
    : oddsHistory;

  // Normalize team names for matching
  const homeNorm = homeTeam.toLowerCase();
  const awayNorm = awayTeam.toLowerCase();

  // Group data by time, then by bookmaker
  const timeMap = new Map<string, LineChartDataPoint>();
  const latestByBookmaker = new Map<string, { home: number | null; away: number | null; timestamp: string; isSharp: boolean }>();
  const bookmakersSet = new Set<string>();

  for (const odds of filtered) {
    const outcomeLower = odds.outcomeName?.toLowerCase() || '';

    // Skip Over/Under totals
    if (outcomeLower === 'over' || outcomeLower === 'under') {
      continue;
    }

    const bookmaker = odds.bookmaker || 'Unknown';
    bookmakersSet.add(bookmaker);

    const timeNum = new Date(odds.timestamp).getTime();
    const time = new Date(odds.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    // Determine if this is home or away
    let isHome = false;
    let isAway = false;

    if (outcomeLower.includes(homeNorm) || homeNorm.includes(outcomeLower) ||
        outcomeLower.includes('home') || outcomeLower === 'h2h_home') {
      isHome = true;
    } else if (outcomeLower.includes(awayNorm) || awayNorm.includes(outcomeLower) ||
               outcomeLower.includes('away') || outcomeLower === 'h2h_away') {
      isAway = true;
    }

    if (!isHome && !isAway) continue;

    // Get or create time point
    const timeKey = `${timeNum}`;
    if (!timeMap.has(timeKey)) {
      timeMap.set(timeKey, { time, timeNum });
    }
    const point = timeMap.get(timeKey)!;

    // Set the value for this bookmaker
    const fieldKey = isHome ? `${bookmaker}_home` : `${bookmaker}_away`;
    point[fieldKey] = odds.price;

    // Track latest for live odds panel
    const isSharp = odds.isSharp ?? SHARP_BOOK_NAMES.some(s => bookmaker.toLowerCase().includes(s));

    if (!latestByBookmaker.has(bookmaker) ||
        new Date(odds.timestamp).getTime() > new Date(latestByBookmaker.get(bookmaker)!.timestamp).getTime()) {
      const existing = latestByBookmaker.get(bookmaker) || { home: null, away: null, timestamp: odds.timestamp, isSharp };
      latestByBookmaker.set(bookmaker, {
        ...existing,
        timestamp: odds.timestamp,
        isSharp,
        home: isHome ? odds.price : existing.home,
        away: isAway ? odds.price : existing.away,
      });
    } else if (latestByBookmaker.has(bookmaker)) {
      const existing = latestByBookmaker.get(bookmaker)!;
      if (isHome && existing.home === null) {
        existing.home = odds.price;
      }
      if (isAway && existing.away === null) {
        existing.away = odds.price;
      }
    }
  }

  // Convert to array and sort by time
  const lineChartData = Array.from(timeMap.values()).sort((a, b) => a.timeNum - b.timeNum);

  // Convert live odds to array, sorted by sharp books first
  const liveOdds: LiveOdds[] = Array.from(latestByBookmaker.entries())
    .map(([bookmaker, data]) => ({
      bookmaker,
      homePrice: data.home,
      awayPrice: data.away,
      isSharp: data.isSharp,
      timestamp: data.timestamp,
    }))
    .sort((a, b) => {
      // Sharp books first, then alphabetical
      if (a.isSharp && !b.isSharp) return -1;
      if (!a.isSharp && b.isSharp) return 1;
      return a.bookmaker.localeCompare(b.bookmaker);
    });

  return {
    lineChartData,
    liveOdds,
    bookmakersInData: Array.from(bookmakersSet),
  };
}

/**
 * Calculate Polymarket 24-hour price change
 */
export function calculatePolymarket24hChange(
  priceHistory: PriceHistoryItem[] | undefined
): number | null {
  if (!priceHistory || priceHistory.length === 0) return null;

  const now = Date.now();
  const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

  // Get current price (latest timestamp)
  const sortedByTime = [...priceHistory].sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const currentPrice = sortedByTime[0]?.midPrice;

  // Get price from 24h ago (or earliest available)
  const oldPrices = priceHistory.filter(p =>
    new Date(p.timestamp).getTime() <= twentyFourHoursAgo
  );
  const oldPrice = oldPrices.length > 0
    ? oldPrices[oldPrices.length - 1].midPrice
    : sortedByTime[sortedByTime.length - 1]?.midPrice;

  if (!currentPrice || !oldPrice) return null;

  return ((currentPrice - oldPrice) / oldPrice) * 100;
}

/**
 * Calculate current price gap between Polymarket and sportsbook odds
 */
export function calculatePriceGap(
  polymarketPrice: number | null,
  oddsHistory: Array<{
    price: number;
    timestamp: string;
  }>
): number | null {
  if (!polymarketPrice || oddsHistory.length === 0) return null;

  // Get most recent odds
  const recentOdds = [...oddsHistory].sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Calculate average implied probability from recent odds
  const avgImpliedProb = recentOdds.slice(0, 10).reduce((sum, o) => {
    const impliedProb = 1 / o.price;
    return sum + impliedProb;
  }, 0) / Math.min(10, recentOdds.length);

  // Gap = (Polymarket price - Odds implied prob) as percentage
  return (polymarketPrice - avgImpliedProb) * 100;
}

/**
 * Process Polymarket price history for chart display
 */
export function processPolymarketPrices(
  priceHistory: PriceHistoryItem[],
  homeTeam?: string,
  awayTeam?: string
): PolymarketChartData[] {
  // Determine which outcomes map to which teams
  // Polymarket typically uses team names or "Yes"/"No" for moneyline
  const outcomeToTeam = new Map<string, 'home' | 'away'>();

  // First pass: analyze outcomes to determine mapping
  const uniqueOutcomes = new Set<string>();
  for (const price of priceHistory) {
    uniqueOutcomes.add(price.outcome);
  }

  // Try to match outcomes to teams
  for (const outcome of uniqueOutcomes) {
    const outcomeLower = outcome.toLowerCase();
    const homeNorm = homeTeam?.toLowerCase() || '';
    const awayNorm = awayTeam?.toLowerCase() || '';

    // Check if outcome matches team name
    if (homeTeam && (outcomeLower.includes(homeNorm) || homeNorm.includes(outcomeLower))) {
      outcomeToTeam.set(outcome, 'home');
    } else if (awayTeam && (outcomeLower.includes(awayNorm) || awayNorm.includes(outcomeLower))) {
      outcomeToTeam.set(outcome, 'away');
    } else if (outcomeLower === 'yes' || outcomeLower.includes('home') || outcomeLower.includes('will win')) {
      outcomeToTeam.set(outcome, 'home');
    } else if (outcomeLower === 'no' || outcomeLower.includes('away')) {
      outcomeToTeam.set(outcome, 'away');
    }
  }

  // If we still have unmapped outcomes, assign first to home, second to away
  const unmapped = Array.from(uniqueOutcomes).filter(o => !outcomeToTeam.has(o));
  if (unmapped.length > 0 && !Array.from(outcomeToTeam.values()).includes('home')) {
    outcomeToTeam.set(unmapped[0], 'home');
  }
  if (unmapped.length > 1 && !Array.from(outcomeToTeam.values()).includes('away')) {
    outcomeToTeam.set(unmapped[1], 'away');
  }

  // Determine labels for chart
  let homeLabel = homeTeam || 'Yes';
  let awayLabel = awayTeam || 'No';

  // If outcomes are team names, use them directly
  for (const [outcome, team] of outcomeToTeam) {
    const outcomeLower = outcome.toLowerCase();
    if (team === 'home' && outcomeLower !== 'yes' && !outcomeLower.includes('home')) {
      homeLabel = outcome; // Use the actual outcome name (team name)
    } else if (team === 'away' && outcomeLower !== 'no' && !outcomeLower.includes('away')) {
      awayLabel = outcome;
    }
  }

  // Group by timestamp
  const grouped = new Map<string, { yes: number | null; no: number | null; timeNum: number }>();

  for (const price of priceHistory) {
    const timeNum = new Date(price.timestamp).getTime();
    const time = new Date(price.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    if (!grouped.has(time)) {
      grouped.set(time, { yes: null, no: null, timeNum });
    }

    const entry = grouped.get(time)!;
    const team = outcomeToTeam.get(price.outcome);

    if (team === 'home') {
      entry.yes = price.midPrice;
    } else if (team === 'away') {
      entry.no = price.midPrice;
    } else {
      // Fallback: first outcome becomes "yes", second becomes "no"
      if (entry.yes === null) {
        entry.yes = price.midPrice;
      } else if (entry.no === null) {
        entry.no = price.midPrice;
      }
    }
  }

  return Array.from(grouped.entries())
    .filter(([, { yes, no }]) => yes !== null || no !== null)
    .map(([time, { yes, no, timeNum }]) => ({
      time,
      timeNum,
      yes: yes ?? 0,
      no: no ?? (yes ? 1 - yes : 0),
      homeLabel,
      awayLabel,
    }))
    .sort((a, b) => a.timeNum - b.timeNum);
}

/**
 * Calculate price gap over time between Polymarket and sportsbook odds
 * Uses 1-minute buckets for high-resolution analysis
 */
export function calculatePriceGapOverTime(
  polymarketHistory: PriceHistoryItem[],
  oddsHistory: Array<{
    timestamp: string;
    price: number;
    outcomeName?: string;
  }>
): Array<{ time: string; gap: number; polymarket: number; odds: number; timeNum: number }> {
  const BUCKET_SIZE_MS = 60 * 1000; // 1-minute buckets (changed from 5-min)

  // Get Polymarket "Yes" prices by time
  const pmByTime = new Map<number, number>();
  for (const pm of polymarketHistory) {
    const outcome = pm.outcome.toLowerCase();
    // Accept any first outcome as "yes" if no explicit yes/home/will win
    const isYes = outcome === 'yes' || outcome.includes('home') || outcome.includes('will win');
    // Also accept if this is the first outcome we see (likely the "Yes" side)
    if (isYes || !outcome.includes('no')) {
      const timeKey = Math.floor(new Date(pm.timestamp).getTime() / BUCKET_SIZE_MS);
      if (!pmByTime.has(timeKey) || pm.midPrice > 0) {
        pmByTime.set(timeKey, pm.midPrice);
      }
    }
  }

  // Get Odds implied probability by time
  // Use average implied probability across bookmakers for each time bucket
  const oddsByTime = new Map<number, { sum: number; count: number }>();
  for (const odds of oddsHistory) {
    const outcomeLower = odds.outcomeName?.toLowerCase() || '';
    // Accept home outcomes or any non-away outcome (for home team implied probability)
    const isHomeOutcome = outcomeLower.includes('home') ||
      outcomeLower === 'h2h_home' ||
      (!outcomeLower.includes('away') && !outcomeLower.includes('draw') && !outcomeLower.includes('over') && !outcomeLower.includes('under'));

    if (isHomeOutcome && odds.price > 1) {
      const timeKey = Math.floor(new Date(odds.timestamp).getTime() / BUCKET_SIZE_MS);
      const impliedProb = 1 / odds.price;

      if (!oddsByTime.has(timeKey)) {
        oddsByTime.set(timeKey, { sum: 0, count: 0 });
      }
      const bucket = oddsByTime.get(timeKey)!;
      bucket.sum += impliedProb;
      bucket.count += 1;
    }
  }

  // Convert odds buckets to averages
  const oddsAvgByTime = new Map<number, number>();
  for (const [timeKey, bucket] of oddsByTime) {
    if (bucket.count > 0) {
      oddsAvgByTime.set(timeKey, bucket.sum / bucket.count);
    }
  }

  // Find overlapping time points and calculate gap
  const gapData: Array<{ time: string; gap: number; polymarket: number; odds: number; timeNum: number }> = [];

  for (const [timeKey, pmPrice] of pmByTime) {
    // Find nearest odds price within 3 minutes (3 buckets at 1-min each)
    let nearestOdds: number | null = null;
    for (let offset = 0; offset <= 3; offset++) {
      if (oddsAvgByTime.has(timeKey + offset)) {
        nearestOdds = oddsAvgByTime.get(timeKey + offset)!;
        break;
      }
      if (oddsAvgByTime.has(timeKey - offset)) {
        nearestOdds = oddsAvgByTime.get(timeKey - offset)!;
        break;
      }
    }

    if (nearestOdds !== null) {
      const gap = (pmPrice - nearestOdds) * 100; // Convert to percentage
      const timestamp = new Date(timeKey * BUCKET_SIZE_MS);
      gapData.push({
        time: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        timeNum: timeKey,
        gap,
        polymarket: pmPrice * 100,
        odds: nearestOdds * 100,
      });
    }
  }

  return gapData.sort((a, b) => a.timeNum - b.timeNum);
}

/**
 * Process RLM signals for chart display
 */
export function processSignalsForChart(
  signals: SignalItem[]
): Array<{ time: string; strength: number; type: string; isActive: number; timeNum: number }> {
  return signals
    .map(signal => ({
      time: new Date(signal.detectedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      timeNum: new Date(signal.detectedAt).getTime(),
      strength: signal.strength * 100, // Convert to percentage
      type: signal.signalType,
      isActive: signal.isActive !== false ? 100 : 0, // For bar chart (default to active)
    }))
    .sort((a, b) => a.timeNum - b.timeNum);
}

/**
 * Get color for a bookmaker based on its name
 */
export function getBookmakerColor(bookmaker: string, isHome: boolean): string {
  const key = bookmaker.toLowerCase();
  for (const [name, colors] of Object.entries(BOOKMAKER_COLORS)) {
    if (key.includes(name)) {
      return isHome ? colors.home : colors.away;
    }
  }
  return isHome ? BOOKMAKER_COLORS.default.home : BOOKMAKER_COLORS.default.away;
}
